import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { useNavigate } from "react-router-dom";
import { Loader2, Save, Eye, EyeOff, Image as ImageIcon, ArrowLeft } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const primaryBtn =
  "bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-60";
const secondaryBtn =
  "bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2";
const labelCls = "text-xs tracking-[0.2em] uppercase font-bold text-slate-400";

const sectionVariants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

const SAMPLE_VALUES = ["Saleem", "Gold Membership", "31-08-2026", "1500", "Marol Branch"];

function fillPlaceholders(text) {
  if (!text) return "";
  return text.replace(/{{\s*(\d+)\s*}}/g, (_, n) => {
    const index = parseInt(n, 10) - 1;
    return SAMPLE_VALUES[index] || `{{${n}}}`;
  });
}

function TemplatePreview({ template }) {
  if (!template) {
    return (
      <div className="text-xs text-slate-400 italic py-6 text-center">
        No template data to preview.
      </div>
    );
  }

  const bodyText = fillPlaceholders(template.body_text);
  const headerText = fillPlaceholders(template.header_text);

  return (
    <div className="bg-[#0b141a] rounded-lg p-4 flex justify-center">
      <div className="max-w-[300px] w-full">
        <div className="bg-[#005c4b] rounded-lg rounded-tr-none p-2.5 shadow-md">
          {template.header_type === "IMAGE" && (
            <div className="w-full h-32 bg-slate-600 rounded-md mb-2 flex items-center justify-center">
              <ImageIcon className="h-8 w-8 text-slate-300" />
            </div>
          )}

          {template.header_type === "TEXT" && headerText && (
            <p className="text-white font-bold text-sm mb-1.5">{headerText}</p>
          )}

          <p className="text-white text-[13px] leading-snug whitespace-pre-wrap">
            {bodyText || "(no body text)"}
          </p>

          {template.footer_text && (
            <p className="text-slate-300 text-[11px] mt-1.5">{template.footer_text}</p>
          )}

          <div className="flex justify-end mt-1">
            <span className="text-[10px] text-slate-300">11:42 AM</span>
          </div>
        </div>

        {template.buttons && template.buttons.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {template.buttons.map((b, i) => (
              <div
                key={i}
                className="bg-[#005c4b] text-center text-[13px] text-[#53bdeb] font-medium py-2 rounded-md"
              >
                {b.text}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AutomationSettings() {
  const navigate = useNavigate();
  const { activeBranchId } = useBranch();
  const [settings, setSettings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [previewOpenKey, setPreviewOpenKey] = useState("");

  const load = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    try {
      const [settingsRes, templatesRes] = await Promise.all([
        api.get("/automation-settings", { params: { branch_id: activeBranchId } }),
        api.get("/templates", { params: { branch_id: activeBranchId } }),
      ]);
      setSettings(settingsRes.data);
      setTemplates(templatesRes.data);
    } finally {
      setLoading(false);
    }
  }, [activeBranchId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateLocal = (triggerKey, patch) => {
    setSettings(settings.map((s) =>
      s.trigger_key === triggerKey ? { ...s, ...patch } : s
    ));
  };

  const save = async (setting) => {
    setSavingKey(setting.trigger_key);
    try {
      await api.put("/automation-settings", {
        branch_id: activeBranchId,
        trigger_key: setting.trigger_key,
        template_name: setting.template_name,
        is_active: setting.is_active,
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingKey("");
    }
  };

  const togglePreview = (triggerKey) => {
    setPreviewOpenKey((current) => (current === triggerKey ? "" : triggerKey));
  };

  return (
    <div data-testid="automation-settings-page">
      <button
        data-testid="back-to-settings-button"
        onClick={() => navigate("/settings")}
        className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white transition-all duration-150 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Settings
      </button>
      <div className="mb-8">
        <h1 className="font-heading text-4xl tracking-tight font-black text-white">
          Automation Settings
        </h1>
        <p className="text-sm text-slate-400 mt-2">
          Pick which template each automated trigger sends, and turn any trigger on
          or off. No code changes needed — just adjust and save.
        </p>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin inline" />
        </div>
      ) : (
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="max-w-2xl space-y-5">
          {settings.map((s) => {
            const selectedTemplate = templates.find((t) => t.name === s.template_name);
            const previewOpen = previewOpenKey === s.trigger_key;

            return (
              <motion.div
                key={s.trigger_key}
                variants={sectionVariants}
                data-testid={`automation-setting-row-${s.trigger_key}`}
                className={`bg-[#141414] border rounded-lg shadow-sm p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30 ${
                  s.is_active ? "border-white/10 hover:border-white/20" : "border-white/10 opacity-60"
                }`}
              >
                <div className="flex items-center justify-between">
                  <label className={labelCls}>{s.label}</label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-400">
                      {s.is_active ? "On" : "Off"}
                    </span>
                    <Switch
                      data-testid={`automation-setting-toggle-${s.trigger_key}`}
                      checked={s.is_active}
                      onCheckedChange={(checked) => updateLocal(s.trigger_key, { is_active: checked })}
                    />
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-3">
                  <Select
                    value={s.template_name}
                    onValueChange={(v) => updateLocal(s.trigger_key, { template_name: v })}
                    disabled={!s.is_active}
                  >
                    <SelectTrigger
                      data-testid={`automation-setting-select-${s.trigger_key}`}
                      className="flex-1"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.name}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <button
                    type="button"
                    data-testid={`automation-setting-preview-${s.trigger_key}`}
                    className={secondaryBtn}
                    onClick={() => togglePreview(s.trigger_key)}
                  >
                    {previewOpen ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    Preview
                  </button>

                  <button
                    data-testid={`automation-setting-save-${s.trigger_key}`}
                    className={primaryBtn}
                    onClick={() => save(s)}
                    disabled={savingKey === s.trigger_key}
                  >
                    {savingKey === s.trigger_key ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save
                  </button>
                </div>

                <AnimatePresence initial={false}>
                {previewOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4">
                      <TemplatePreview template={selectedTemplate} />
                      <p className="text-[11px] text-slate-400 mt-2 text-center">
                        Sample data shown — real messages use the member's actual name, plan, and details.
                      </p>
                    </div>
                  </motion.div>
                )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}